import React, { useCallback, useState } from 'react';
import { CancelIcon } from '../../shared/icons';
import pullRunsService from '../../shared/services/pullRunsService';
import type { PullRun } from '../../shared/services/pullRunsService';

export interface PullRunsCancelModalProps {
  isOpen: boolean;
  pullRun: PullRun;
  onClose: () => void;
  onCancelSuccess?: (pullRunId: number) => void;
  onCancelError?: (pullRunId: number, error: string) => void;
}

export const PullRunsCancelModal: React.FC<PullRunsCancelModalProps> = ({
  isOpen,
  pullRun,
  onClose,
  onCancelSuccess,
  onCancelError
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (isSubmitting) return; // Не позволяем закрыть во время отправки
    setReason('');
    setError(null);
    onClose();
  }, [isSubmitting, onClose]);

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleClose, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await pullRunsService.cancelPullRun(pullRun.id, reason);

      if (response.error) {
        const errorMessage = response.error;
        setError(errorMessage);
        onCancelError?.(pullRun.id, errorMessage);
      } else if (response.data) {
        onCancelSuccess?.(pullRun.id);
        onClose(); // Закрываем модальное окно при успехе
      } else {
        onCancelError?.(pullRun.id, 'Неожиданный ответ сервера');
        setError('Неожиданный ответ сервера');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      setError(errorMessage);
      onCancelError?.(pullRun.id, errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop-custom" onClick={handleClose}>
      <div 
        className="modal-dialog-custom animate-in" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header-custom">
          <div className="modal-header-avatar-custom">
            <CancelIcon width={24} height={24} />
          </div>
          <div className="modal-header-content-custom">
            <h2 className="modal-title-custom">Отменить задачу</h2>
            <p className="modal-subtitle-custom">
              Вы уверены, что хотите отменить выполнение задачи?
            </p>
          </div>
          <button
            type="button"
            className="modal-close-custom"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body-custom">
            <div className="form-grid">
              <div className="form-group-custom">
                <label className="form-label-custom">
                  Информация о задаче
                </label>
                <div className="form-input-custom readonly">
                  <strong>ID задачи:</strong> {pullRun.id}
                  {pullRun.integrationName && (
                    <div style={{ marginTop: '4px' }}>
                      <strong>Интеграция:</strong> {pullRun.integrationName}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group-custom">
                <label className="form-label-custom">
                  Причина отмены (необязательно)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="form-textarea-custom"
                  placeholder="Укажите причину отмены задачи (например: задача выполняется слишком долго, найдена ошибка в данных, и т.д.)"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              {error && (
                <div className="form-error-custom">
                  {error}
                </div>
              )}

              <div className="alert alert-warning">
                <strong>Внимание!</strong> Отмена задачи приведет к немедленному прерыванию процесса и не может быть отменена.
              </div>
            </div>
          </div>

          <div className="modal-footer-custom">
            <button
              type="button"
              className="btn-secondary-custom"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="btn-primary-custom"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner"></span>
                  Отменяю...
                </>
              ) : (
                <>
                  <CancelIcon width={16} height={16} />
                  Отменить задачу
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PullRunsCancelModal;
